import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { PayoutDetailsEntity } from "@modules/finances/financesPayout/entities/payoutEntity"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import dayjs from "dayjs"
import { useImperativeHandle, useState } from "react"
import { Link } from "react-router"
import { Text } from "@/components/ui/baseUi/text"

export type PayoutDetailsHelpModalRef = {
  open: () => void
}

type Props = {
  payoutDetails: PayoutDetailsEntity
  ref: React.Ref<PayoutDetailsHelpModalRef>
}

export function PayoutDetailsHelpModal({ payoutDetails, ref }: Props) {
  const language = useLanguage()
  const [showModal, setShowModal] = useState(false)

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="w-[510px]">
        <DialogHeader className="mt-4 flex flex-col gap-2">
          <DialogTitle>
            <Text
              fontStyle="TL"
              className="flex content-center justify-center pt-4 text-center text-textDefault-heading"
            >
              {word("finances.payoutHelpModal.searchTransfer")}
            </Text>
          </DialogTitle>
          <DialogDescription>
            <Text fontStyle="BM" className="flex content-center justify-center text-textDefault-heading">
              {word("finances.payoutHelpModal.content", { bankName: payoutDetails.bankName })}
            </Text>
          </DialogDescription>
        </DialogHeader>

        <Text fontStyle="BM" className="mt-4 flex w-full text-textDefault-heading">
          {payoutDetails.bankName}
        </Text>
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex justify-between">
            <div className="flex flex-col gap-1">
              <Text fontStyle="BS" className="text-textDefault-caption">
                {word("finances.payoutHelpModal.accountNumber")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-heading">
                {payoutDetails.getFormatedDigits()}
              </Text>
            </div>
            <div className="flex flex-col gap-1">
              <Text fontStyle="BS" className="text-textDefault-caption">
                {word("finances.payoutHelpModal.routingNumber")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-heading">
                {payoutDetails.routingNumber ?? word("global.notAvailable")}
              </Text>
            </div>
          </div>
          <div className="flex justify-between">
            <div className="flex flex-col gap-1">
              <Text fontStyle="BS" className="text-textDefault-caption">
                {word("finances.payoutDetails.transferMade")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-heading">
                {dayjs(payoutDetails.createdAt).format("ll")}
              </Text>
            </div>
            <div className="flex flex-col gap-1">
              <Text fontStyle="BS" className="text-textDefault-caption">
                {word("finances.payoutHelpModal.transferAmount")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-heading">
                {formatCurrency(payoutDetails.amount, payoutDetails.currency, language)}
              </Text>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("finances.payoutDetails.internalNote")}
            </Text>
            <Text fontStyle="TS" className="text-textDefault-heading capitalize">
              {payoutDetails.note?.toLowerCase() ?? "-"}
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("finances.payoutDetails.transferTrackingId")}
            </Text>
            <Text fontStyle="TS" className="text-textDefault-heading">
              {payoutDetails.transferTrackingId ?? "-"}
            </Text>
          </div>
        </div>
        <div className="mt-4 flex justify-between gap-4">
          <Text fontStyle="BM" className="text-textDefault-heading">
            {word("finances.payoutHelpModal.contactText")}
            <Link to={word("global.support.zendesk")} className="text-textDefault-caption underline" target="_blank">
              {word("finances.payoutHelpModal.contactUs")}
            </Link>
          </Text>

          <Button
            variant="primary"
            onClick={() => {
              setShowModal(false)
            }}
          >
            {word("finances.payoutHelpModal.closeModal")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
